/**
 * Streaming Monitoring Event Types
 *
 * Discriminated union types for real-time workflow run monitoring events
 * received via SSE from the runtime's RunMonitoring endpoint.
 *
 * Backend model: RunMonitoringEvent (C#, PR #389)
 * Wire format: The C# model is FLAT — all fields live on one class with
 * `Required.Default`. The `kind` field uses `[JsonConverter(typeof(StringEnumConverter))]`
 * so it arrives as PascalCase strings ("RunSnapshot", "ActionCompleted", etc.).
 * The SSE `event:` field is also set to the PascalCase kind.
 *
 * Design:
 * - Zod schemas for runtime validation of untrusted SSE payloads
 * - Discriminated union on `kind` camelCase string literal
 * - Parser maps PascalCase wire kind → camelCase before validation
 * - Non-discriminator fields are nullable+optional (C# flat model, not all set per kind)
 * - Type guards for narrowing in event handlers
 * - StreamingRunState for progressive state built from events
 */

import { z } from 'zod';

import type { NodeExecutionStatus } from './editorRunData';
import type { AgentIterationData, EditorRunState, NodeRunData } from './editorRunData';
import type { RunStatus } from './monitoring';

// =============================================================================
// EVENT KIND
// =============================================================================

/**
 * String-literal event kinds used as the discriminator.
 * Maps 1:1 to the C# RunMonitoringEventKind enum values.
 */
export const RunMonitoringEventKindSchema = z.enum([
  'runSnapshot',
  'triggerFired',
  'actionStarted',
  'actionCompleted',
  'repetitionStarted',
  'repetitionCompleted',
  'actionRetried',
  'chatMessage',
  'runCompleted',
]);
export type RunMonitoringEventKind = z.infer<typeof RunMonitoringEventKindSchema>;

/**
 * Wire format uses PascalCase string kind values (C# StringEnumConverter).
 * This maps them to our camelCase string literals.
 */
const WIRE_KIND_MAP: ReadonlyMap<string, RunMonitoringEventKind> = new Map([
  ['RunSnapshot', 'runSnapshot'],
  ['TriggerFired', 'triggerFired'],
  ['ActionStarted', 'actionStarted'],
  ['ActionCompleted', 'actionCompleted'],
  ['RepetitionStarted', 'repetitionStarted'],
  ['RepetitionCompleted', 'repetitionCompleted'],
  ['ActionRetried', 'actionRetried'],
  ['ChatMessage', 'chatMessage'],
  ['RunCompleted', 'runCompleted'],
]);

/**
 * Fallback: numeric wire kind values (in case backend changes serialization).
 */
const WIRE_NUMERIC_KIND_MAP: ReadonlyMap<number, RunMonitoringEventKind> = new Map([
  [1, 'runSnapshot'],
  [2, 'triggerFired'],
  [3, 'actionStarted'],
  [4, 'actionCompleted'],
  [5, 'repetitionStarted'],
  [6, 'repetitionCompleted'],
  [7, 'actionRetried'],
  [8, 'chatMessage'],
  [9, 'runCompleted'],
]);

// =============================================================================
// PER-EVENT SCHEMAS
// =============================================================================
//
// The C# model is FLAT: all fields exist on every JSON object, with irrelevant
// ones set to null. Per-variant fields are nullable().optional() because:
// - Fields not relevant to a kind are null on the wire
// - Even relevant fields may be null if the emitter doesn't set them
//   (e.g., ActionStarted only sets Kind, Timestamp, ActionName)
// - JToken fields (actionError, retryError, error) can be any JSON shape
//
// Zod strips unrecognized keys from other variants automatically.

/** Helper for nullable optional string */
const nStr = z.string().nullable().optional();
/** Helper for nullable optional number */
const nNum = z.number().nullable().optional();
/** Helper for JToken fields (any JSON shape) */
const jToken = z.unknown().nullable().optional();

const RunSnapshotEventSchema = z.object({
  kind: z.literal('runSnapshot'),
  timestamp: z.string(),
  runId: nStr,
  flowName: nStr,
  runStatus: nStr,
});

const TriggerFiredEventSchema = z.object({
  kind: z.literal('triggerFired'),
  timestamp: z.string(),
  triggerName: nStr,
  triggerStatus: nStr,
  triggerStartTime: nStr,
});

const ActionStartedEventSchema = z.object({
  kind: z.literal('actionStarted'),
  timestamp: z.string(),
  actionName: nStr,
  actionStatus: nStr,
  actionStartTime: nStr,
  actionType: nStr,
});

const ActionCompletedEventSchema = z.object({
  kind: z.literal('actionCompleted'),
  timestamp: z.string(),
  actionName: nStr,
  actionStatus: nStr,
  actionStartTime: nStr,
  actionEndTime: nStr,
  actionType: nStr,
  actionError: jToken,
});

const RepetitionStartedEventSchema = z.object({
  kind: z.literal('repetitionStarted'),
  timestamp: z.string(),
  repetitionName: nStr,
  repetitionIndex: nNum,
  repetitionStatus: nStr,
  scopeActionName: nStr,
});

const RepetitionCompletedEventSchema = z.object({
  kind: z.literal('repetitionCompleted'),
  timestamp: z.string(),
  repetitionName: nStr,
  repetitionIndex: nNum,
  repetitionStatus: nStr,
  scopeActionName: nStr,
});

const ActionRetriedEventSchema = z.object({
  kind: z.literal('actionRetried'),
  timestamp: z.string(),
  actionName: nStr,
  retryCount: nNum,
  nextRetryTime: nStr,
  retryError: jToken,
});

const ChatMessageEventSchema = z.object({
  kind: z.literal('chatMessage'),
  timestamp: z.string(),
  actionName: nStr,
  chatRole: nStr,
  chatContent: nStr,
  chatMessageSequenceId: nStr, // C# type is string, not int
});

const RunCompletedEventSchema = z.object({
  kind: z.literal('runCompleted'),
  timestamp: z.string(),
  finalStatus: nStr,
  code: nStr,
  endTime: nStr,
  error: jToken,
});

// =============================================================================
// DISCRIMINATED UNION
// =============================================================================

/**
 * Zod discriminated union for runtime validation of SSE payloads.
 * Each variant carries exactly the fields for that event kind.
 */
export const RunMonitoringEventSchema = z.discriminatedUnion('kind', [
  RunSnapshotEventSchema,
  TriggerFiredEventSchema,
  ActionStartedEventSchema,
  ActionCompletedEventSchema,
  RepetitionStartedEventSchema,
  RepetitionCompletedEventSchema,
  ActionRetriedEventSchema,
  ChatMessageEventSchema,
  RunCompletedEventSchema,
]);

// =============================================================================
// INFERRED TYPES
// =============================================================================

export type RunSnapshotEvent = z.infer<typeof RunSnapshotEventSchema>;
export type TriggerFiredEvent = z.infer<typeof TriggerFiredEventSchema>;
export type ActionStartedEvent = z.infer<typeof ActionStartedEventSchema>;
export type ActionCompletedEvent = z.infer<typeof ActionCompletedEventSchema>;
export type RepetitionStartedEvent = z.infer<typeof RepetitionStartedEventSchema>;
export type RepetitionCompletedEvent = z.infer<typeof RepetitionCompletedEventSchema>;
export type ActionRetriedEvent = z.infer<typeof ActionRetriedEventSchema>;
export type ChatMessageEvent = z.infer<typeof ChatMessageEventSchema>;
export type RunCompletedEvent = z.infer<typeof RunCompletedEventSchema>;

/** Union of all monitoring event types */
export type RunMonitoringEvent = z.infer<typeof RunMonitoringEventSchema>;

// =============================================================================
// TYPE GUARDS
// =============================================================================

export function isRunSnapshotEvent(event: RunMonitoringEvent): event is RunSnapshotEvent {
  return event.kind === 'runSnapshot';
}

export function isTriggerFiredEvent(event: RunMonitoringEvent): event is TriggerFiredEvent {
  return event.kind === 'triggerFired';
}

export function isActionStartedEvent(event: RunMonitoringEvent): event is ActionStartedEvent {
  return event.kind === 'actionStarted';
}

export function isActionCompletedEvent(event: RunMonitoringEvent): event is ActionCompletedEvent {
  return event.kind === 'actionCompleted';
}

export function isRepetitionStartedEvent(
  event: RunMonitoringEvent
): event is RepetitionStartedEvent {
  return event.kind === 'repetitionStarted';
}

export function isRepetitionCompletedEvent(
  event: RunMonitoringEvent
): event is RepetitionCompletedEvent {
  return event.kind === 'repetitionCompleted';
}

export function isActionRetriedEvent(event: RunMonitoringEvent): event is ActionRetriedEvent {
  return event.kind === 'actionRetried';
}

export function isChatMessageEvent(event: RunMonitoringEvent): event is ChatMessageEvent {
  return event.kind === 'chatMessage';
}

export function isRunCompletedEvent(event: RunMonitoringEvent): event is RunCompletedEvent {
  return event.kind === 'runCompleted';
}

// =============================================================================
// WIRE FORMAT PARSING
// =============================================================================

/** Result of parsing an SSE data payload */
export type ParseResult =
  | { success: true; event: RunMonitoringEvent }
  | { success: false; error: string; raw: string };

/**
 * Parse a raw SSE `data:` payload into a typed RunMonitoringEvent.
 *
 * Handles:
 * 1. JSON.parse failure (malformed payload)
 * 2. PascalCase `kind` → camelCase mapping (C# StringEnumConverter)
 * 3. Numeric `kind` fallback (in case backend changes serialization)
 * 4. Zod validation (missing/wrong fields)
 * 5. Unknown/unspecified kind values
 */
export function parseRunMonitoringEvent(data: string): ParseResult {
  let raw: unknown;
  try {
    raw = JSON.parse(data);
  } catch {
    return { success: false, error: 'Invalid JSON', raw: data };
  }

  if (typeof raw !== 'object' || raw === null) {
    return { success: false, error: 'Payload is not an object', raw: data };
  }

  const wireObj = raw as Record<string, unknown>;
  const wireKind = wireObj['kind'];

  // Map PascalCase string kind → camelCase (primary path)
  if (typeof wireKind === 'string') {
    // 'Unspecified' is the C# enum default (0) — skip it
    if (wireKind === 'Unspecified' || wireKind === 'unspecified') {
      return { success: false, error: 'Unspecified event kind — skipping', raw: data };
    }
    const mapped = WIRE_KIND_MAP.get(wireKind);
    if (mapped !== undefined) {
      wireObj['kind'] = mapped;
    }
    // else: might already be camelCase — Zod will validate
  } else if (typeof wireKind === 'number') {
    // Fallback for numeric kind. 0 = Unspecified (C# enum default) — skip silently.
    if (wireKind === 0) {
      return { success: false, error: 'Unspecified event kind (0) — skipping', raw: data };
    }
    const mapped = WIRE_NUMERIC_KIND_MAP.get(wireKind);
    if (mapped === undefined) {
      return { success: false, error: `Unknown numeric event kind: ${wireKind}`, raw: data };
    }
    wireObj['kind'] = mapped;
  } else {
    return { success: false, error: `Invalid kind type: ${typeof wireKind}`, raw: data };
  }

  const result = RunMonitoringEventSchema.safeParse(wireObj);
  if (result.success) {
    return { success: true, event: result.data };
  }

  const issues = result.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; ');
  return { success: false, error: `Validation failed: ${issues}`, raw: data };
}

// =============================================================================
// STREAMING RUN STATE
// =============================================================================

/**
 * Retry attempt tracked during streaming
 */
export interface StreamingRetryAttempt {
  count: number;
  nextRetryTime: string;
  error: string | null; // JToken → stringified
  timestamp: string;
}

/**
 * Chat message received during streaming
 */
export interface StreamingChatMessage {
  role: string;
  content: string;
  sequenceId: string; // C# type is string
  timestamp: string;
}

/**
 * Repetition state within a scope action (Foreach, Until, etc.)
 */
export interface StreamingRepetitionState {
  name: string;
  index: number;
  status: NodeExecutionStatus;
  startTime: string;
  endTime?: string | undefined;
}

/**
 * Per-node state built progressively from action events.
 */
export interface StreamingNodeState {
  actionName: string;
  actionType: string;
  status: NodeExecutionStatus;
  startTime: string;
  endTime?: string | undefined;
  error?: string | undefined;
  retries: StreamingRetryAttempt[];
  chatMessages: StreamingChatMessage[];
  repetitions: Map<number, StreamingRepetitionState>;
}

/**
 * Progressive run state built from a stream of monitoring events.
 * This is the live view — updated on every event.
 */
export interface StreamingRunState {
  /** Run ID from the initial RunSnapshot event */
  runId: string;
  /** Workflow name */
  flowName: string;
  /** Current run status (updated on RunSnapshot and RunCompleted) */
  status: RunStatus;
  /** Trigger info (set on TriggerFired) */
  trigger?: {
    name: string;
    status: string;
    startTime: string;
  } | undefined;
  /** Per-action node states, keyed by actionName */
  nodes: Map<string, StreamingNodeState>;
  /** Run start time (from first event timestamp or RunSnapshot) */
  startTime: string;
  /** Run end time (set on RunCompleted) */
  endTime?: string | undefined;
  /** Terminal error (set on RunCompleted if failed) */
  error?: string | undefined;
  /** Error code (set on RunCompleted) */
  errorCode?: string | undefined;
  /** Count of events applied (for diagnostics, replaces unbounded eventLog) */
  eventCount: number;
}

// =============================================================================
// STATE BUILDER
// =============================================================================

/**
 * Create an empty StreamingRunState from the initial RunSnapshot.
 * Handles nullable fields — runId and flowName should always be present
 * on RunSnapshot, but we guard against null with fallbacks.
 */
export function createStreamingRunState(snapshot: RunSnapshotEvent): StreamingRunState {
  const runId = snapshot.runId ?? `unknown-${Date.now()}`;
  if (!snapshot.runId) {
    console.warn('[streaming] RunSnapshot missing runId, using synthetic ID:', runId);
  }
  return {
    runId,
    flowName: snapshot.flowName ?? 'unknown',
    status: snapshot.runStatus ? normalizeRunStatus(snapshot.runStatus) : 'running',
    nodes: new Map(),
    startTime: snapshot.timestamp,
    eventCount: 1,
  };
}

/**
 * Apply a monitoring event to the current state, returning a new state.
 * Shallow-copies top-level fields; mutates the nodes Map in place since
 * only the final state per RAF frame is read (intermediate states are never consumed).
 * Handles nullable fields from the flat C# wire model with safe fallbacks.
 */
export function applyMonitoringEvent(
  state: StreamingRunState,
  event: RunMonitoringEvent
): StreamingRunState {
  const next: StreamingRunState = {
    ...state,
    eventCount: state.eventCount + 1,
  };
  // Share the same nodes Map reference — we'll mutate entries directly.
  // This is safe because streamingRunStore.handleEvent is the sole consumer,
  // and it calls set() after this returns, which triggers a new Zustand snapshot.

  switch (event.kind) {
    case 'runSnapshot': {
      if (event.runId) next.runId = event.runId;
      if (event.flowName) next.flowName = event.flowName;
      if (event.runStatus) next.status = normalizeRunStatus(event.runStatus);
      break;
    }

    case 'triggerFired': {
      if (event.triggerName) {
        next.trigger = {
          name: event.triggerName,
          status: event.triggerStatus ?? 'fired',
          startTime: event.triggerStartTime ?? event.timestamp,
        };
      }
      break;
    }

    case 'actionStarted': {
      const actionName = event.actionName;
      if (!actionName) break;
      const existing = state.nodes.get(actionName);
      state.nodes.set(actionName, {
        actionName,
        actionType: event.actionType ?? existing?.actionType ?? 'unknown',
        status: 'running',
        startTime: event.actionStartTime ?? event.timestamp,
        retries: existing?.retries ?? [],
        chatMessages: existing?.chatMessages ?? [],
        repetitions: existing?.repetitions ?? new Map(),
      });
      break;
    }

    case 'actionCompleted': {
      const actionName = event.actionName;
      if (!actionName) break;
      const existing = state.nodes.get(actionName);
      const node: StreamingNodeState = {
        actionName,
        actionType: event.actionType ?? existing?.actionType ?? 'unknown',
        status: event.actionStatus ? normalizeNodeStatus(event.actionStatus) : 'succeeded',
        startTime: event.actionStartTime ?? existing?.startTime ?? event.timestamp,
        retries: existing?.retries ?? [],
        chatMessages: existing?.chatMessages ?? [],
        repetitions: existing?.repetitions ?? new Map(),
      };
      if (event.actionEndTime) node.endTime = event.actionEndTime;
      const errorStr = sanitizeErrorForDisplay(event.actionError);
      if (errorStr) node.error = errorStr;
      state.nodes.set(actionName, node);
      break;
    }

    case 'actionRetried': {
      const actionName = event.actionName;
      if (!actionName) break;
      // Create node on-demand if it doesn't exist yet (event ordering not guaranteed)
      const existing = state.nodes.get(actionName) ?? createDefaultNodeState(actionName, event.timestamp);
      const updated: StreamingNodeState = {
        ...existing,
        retries: [
          ...existing.retries,
          {
            count: event.retryCount ?? existing.retries.length + 1,
            nextRetryTime: event.nextRetryTime ?? '',
            error: sanitizeErrorForDisplay(event.retryError),
            timestamp: event.timestamp,
          },
        ],
      };
      state.nodes.set(actionName, updated);
      break;
    }

    case 'chatMessage': {
      const actionName = event.actionName;
      if (!actionName) break;
      // Create node on-demand if it doesn't exist yet (event ordering not guaranteed)
      const existing = state.nodes.get(actionName) ?? createDefaultNodeState(actionName, event.timestamp);
      const updated: StreamingNodeState = {
        ...existing,
        chatMessages: [
          ...existing.chatMessages,
          {
            role: event.chatRole ?? 'unknown',
            content: event.chatContent ?? '',
            sequenceId: event.chatMessageSequenceId ?? String(existing.chatMessages.length),
            timestamp: event.timestamp,
          },
        ],
      };
      state.nodes.set(actionName, updated);
      break;
    }

    case 'repetitionStarted': {
      const scopeName = event.scopeActionName;
      const repIndex = event.repetitionIndex;
      if (!scopeName || repIndex == null) break;
      const existing = state.nodes.get(scopeName) ?? createDefaultNodeState(scopeName, event.timestamp);
      const reps = new Map(existing.repetitions);
      reps.set(repIndex, {
        name: event.repetitionName ?? `rep-${repIndex}`,
        index: repIndex,
        status: 'running',
        startTime: event.timestamp,
      });
      state.nodes.set(scopeName, { ...existing, repetitions: reps });
      break;
    }

    case 'repetitionCompleted': {
      const scopeName = event.scopeActionName;
      const repIndex = event.repetitionIndex;
      if (!scopeName || repIndex == null) break;
      const existing = state.nodes.get(scopeName) ?? createDefaultNodeState(scopeName, event.timestamp);
      const reps = new Map(existing.repetitions);
      const prev = reps.get(repIndex);
      reps.set(repIndex, {
        name: event.repetitionName ?? prev?.name ?? `rep-${repIndex}`,
        index: repIndex,
        status: event.repetitionStatus ? normalizeNodeStatus(event.repetitionStatus) : 'succeeded',
        startTime: prev?.startTime ?? event.timestamp,
        endTime: event.timestamp,
      });
      state.nodes.set(scopeName, { ...existing, repetitions: reps });
      break;
    }

    case 'runCompleted': {
      if (event.finalStatus) next.status = normalizeRunStatus(event.finalStatus);
      if (event.endTime) next.endTime = event.endTime;
      const errorStr = sanitizeErrorForDisplay(event.error);
      if (errorStr) next.error = errorStr;
      if (event.code) next.errorCode = event.code;
      break;
    }
  }

  return next;
}

// =============================================================================
// CONVERSION: StreamingRunState → EditorRunState
// =============================================================================

/**
 * Convert a StreamingRunState into an EditorRunState for the workflow editor.
 *
 * The editor expects nodeRunData keyed by nodeId (which for streaming is
 * derived from the actionName). This bridges live monitoring into the
 * editor's execution overlay.
 */
export function toEditorRunState(
  streaming: StreamingRunState,
  workflowId: string
): EditorRunState {
  const nodeRunData: Record<string, NodeRunData> = {};

  for (const [actionName, node] of streaming.nodes) {
    const nodeId = actionToNodeId(actionName);
    const entry: NodeRunData = {
      nodeId,
      nodeName: actionName,
      nodeType: node.actionType,
      status: node.status,
    };

    // Assign optional fields only when defined (exactOptionalPropertyTypes)
    if (node.startTime !== undefined) entry.startTime = node.startTime;
    if (node.endTime !== undefined) entry.endTime = node.endTime;
    if (node.endTime !== undefined && node.startTime !== undefined) {
      entry.duration = new Date(node.endTime).getTime() - new Date(node.startTime).getTime();
    }
    if (node.error !== undefined) {
      entry.error = { code: 'ActionFailed', message: node.error };
    }

    // Map foreach/scope repetitions to iteration data
    if (node.repetitions.size > 0) {
      entry.iterationCount = node.repetitions.size;
      entry.iterations = Array.from(node.repetitions.values())
        .sort((a, b) => a.index - b.index)
        .map((rep) => {
          const iter: AgentIterationData = {
            index: rep.index,
            status: rep.status,
          };
          if (rep.startTime !== undefined) iter.startTime = rep.startTime;
          if (rep.endTime !== undefined) iter.endTime = rep.endTime;
          if (rep.endTime !== undefined && rep.startTime !== undefined) {
            iter.duration = new Date(rep.endTime).getTime() - new Date(rep.startTime).getTime();
          }
          return iter;
        });
    }

    nodeRunData[nodeId] = entry;
  }

  const state: EditorRunState = {
    runId: streaming.runId,
    executionId: streaming.runId, // streaming has a single execution context
    workflowId,
    workflowName: streaming.flowName,
    runStatus: streaming.status,
    runStartTime: streaming.startTime,
    loadedAt: new Date().toISOString(),
    nodeRunData,
    pinData: {},
  };

  if (streaming.endTime !== undefined) {
    state.runEndTime = streaming.endTime;
  }

  return state;
}

// =============================================================================
// HELPERS (internal)
// =============================================================================

/**
 * Sanitize a JToken error value for display. Extracts structured message
 * when available, truncates to prevent UI overflow, and avoids leaking
 * internal server details (stack traces, connection strings, file paths).
 */
function sanitizeErrorForDisplay(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return value.length > 0 ? value.substring(0, 500) : null;
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    // Prefer structured .message field (standard error shape)
    const msg = obj['message'] ?? obj['Message'];
    if (typeof msg === 'string' && msg.length > 0) return msg.substring(0, 500);
    // Fall back to .code if no message
    const code = obj['code'] ?? obj['Code'];
    if (typeof code === 'string' && code.length > 0) return code;
  }
  // Fallback: avoid stringifying arbitrary payloads which may contain secrets
  return 'Action failed — see runtime logs for details';
}

/**
 * Create a default node state for events that arrive before ActionStarted
 * (e.g., ChatMessage or ActionRetried from event reordering or late join).
 */
function createDefaultNodeState(actionName: string, timestamp: string): StreamingNodeState {
  return {
    actionName,
    actionType: 'unknown',
    status: 'running',
    startTime: timestamp,
    retries: [],
    chatMessages: [],
    repetitions: new Map(),
  };
}

/**
 * Normalize a wire status string to RunStatus.
 * The runtime sends FlowStatus enum values (PascalCase via .ToString()).
 * FlowStatus has 16 values — map them into our 4-value RunStatus.
 */
function normalizeRunStatus(wireStatus: string): RunStatus {
  const lower = wireStatus.toLowerCase();
  switch (lower) {
    case 'running':
    case 'waiting':
    case 'suspended':
    case 'paused':
      return 'running';
    case 'succeeded':
      return 'succeeded';
    case 'failed':
    case 'faulted':
    case 'timedout':
    case 'aborted':
    case 'terminated':
      return 'failed';
    case 'cancelled':
    case 'canceled':
      return 'cancelled';
    case 'skipped':
    case 'ignored':
      return 'cancelled'; // closest match — didn't run
    default:
      return 'running'; // notspecified, deleted, handedoff — treat as in-progress
  }
}

/**
 * Normalize a wire status string to NodeExecutionStatus.
 * Same source FlowStatus enum, mapped to our 5-value NodeExecutionStatus.
 */
function normalizeNodeStatus(wireStatus: string): NodeExecutionStatus {
  const lower = wireStatus.toLowerCase();
  switch (lower) {
    case 'running':
      return 'running';
    case 'succeeded':
      return 'succeeded';
    case 'failed':
    case 'faulted':
    case 'timedout':
    case 'aborted':
    case 'terminated':
      return 'failed';
    case 'skipped':
    case 'ignored':
      return 'skipped';
    case 'cancelled':
    case 'canceled':
      // First-class cancelled status (renders amber). Previously collapsed
      // to 'skipped', which lost information — see issue #1708.
      return 'cancelled';
    case 'pending':
    case 'waiting':
    case 'suspended':
    case 'paused':
      return 'pending';
    default:
      return 'running';
  }
}

/**
 * Derive a node ID from an action name.
 * Matches the convention used in the workflow definition:
 * action names like "Send_an_email" → nodeId "action-Send_an_email"
 */
function actionToNodeId(actionName: string): string {
  return `action-${actionName}`;
}

// =============================================================================
// ERROR TYPE
// =============================================================================

/**
 * Error from the SSE stream client.
 * `runMayBeActive` tells callers whether the run was triggered server-side
 * (and they should fall back to polling) or the request never reached the server.
 */
export type RunStreamErrorType =
  | 'not-supported'     // 404 — endpoint doesn't exist on this runtime
  | 'connection-failed' // Network error or non-404 HTTP error
  | 'stream-error'      // Error reading the SSE stream mid-flight
  | 'timeout';          // Silence or absolute timeout

export interface RunStreamError {
  type: RunStreamErrorType;
  message: string;
  /** HTTP status if the initial request failed */
  httpStatus?: number | undefined;
  /** Run ID if known (from RunSnapshot before failure) */
  runId?: string | undefined;
}
